import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setBotVoice, VoiceSummary } from '../../services/providerReviewService';

/**
 * Voice devices (plan/VOICE_DEVICE_CLIENT_PLAN.md): the platform's per-bot
 * switch. During the PoC the platform pays for speech-to-text and
 * text-to-speech, so voice is opt-in per bot here. A paired device reaches the
 * bot only while this, the env kill switch, and the provider's own toggle are
 * all on. Turning it off makes paired devices inert at once (they come back
 * when it is switched on again).
 */
const VoiceDevicesSection: React.FC<{ botId: string; initial?: VoiceSummary }> = ({ botId, initial }) => {
  const { t } = useTranslation();
  const [voice, setVoice] = useState<VoiceSummary | undefined>(initial);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!voice) return null;

  const toggle = async () => {
    setActing(true);
    setError(null);
    try {
      setVoice(await setBotVoice(botId, !voice.platformEnabled));
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setActing(false);
    }
  };

  const reachable = voice.envEnabled && voice.platformEnabled && voice.providerEnabled;
  return (
    <section className="detail-block voice-devices">
      <h4>{t('review.voice.title', 'Voice devices (PoC)')}</h4>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className={'verify-pill ' + (reachable ? 'ok' : 'no')}>
          {reachable ? t('review.voice.on', 'enabled') : t('review.voice.off', 'off')}
        </span>
        <button type="button" disabled={acting || !voice.envEnabled} onClick={toggle}>
          {voice.platformEnabled ? t('review.voice.disable', 'Turn voice off') : t('review.voice.enable', 'Turn voice on')}
        </button>
      </div>
      <p className="detail-muted">
        {t('review.voice.hint', 'The platform pays for speech processing during the PoC. When on, the provider sees a voice toggle (default on) and customers can pair devices. Turning it off stops paired devices immediately; they come back when it is turned on again.')}
      </p>
      {!voice.envEnabled && <p className="detail-muted">{t('review.voice.envOff', 'Voice is switched off for this whole environment.')}</p>}
      {voice.platformEnabled && !voice.providerEnabled && (
        <p className="detail-muted">{t('review.voice.providerOff', 'The provider has turned voice off for this bot.')}</p>
      )}
      <div className="voice-stats" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
        <span>{t('review.voice.devices', 'Paired devices')}: <b>{voice.activeDevices}</b></span>
        <span>{t('review.voice.utterances', 'Utterances today')}: <b>{voice.today.utterances}</b></span>
        <span>{t('review.voice.audio', 'Audio in')}: <b>{(voice.today.audioSeconds / 60).toFixed(1)} min</b></span>
        <span>{t('review.voice.cost', 'Est. cost today (platform)')}: <b>${voice.today.estUsd.toFixed(2)}</b></span>
      </div>
      {error && <div className="review-error">{error}</div>}
    </section>
  );
};

export default VoiceDevicesSection;
